import {
  makeObservable,
  observable,
  action,
  computed,
  IObservableArray,
  onBecomeObserved,
  onBecomeUnobserved,
  runInAction,
} from "mobx";
import { liveQuery, Subscription } from "dexie";
import { BaseSyncModelStore } from "@/store/sync/BaseSyncModelStore";
import { SyncModelKind } from "@/store/sync/types";
import { AppSubStoreArgs } from "@/store/types";
import type { SessionModelData, SessionStatus, SessionKind } from "@/store/sessions/types";
import { SessionObservable } from "./SessionObservable";
import { SyncUpdateValue } from "@/store/sync/types";
import { Maybe } from "@/domains/common/types";
import { uuidModule } from "@/modules/uuid";
import { CreateSessionOperation } from "@/store/sync/operations/sessions/CreateSessionOperation";
import { Uuid } from "@/domains/global/identifiers";
import { UpdateSessionOperation } from "@/store/sync/operations/sessions/UpdateSessionOperation";

export class AppStoreSessionStore extends BaseSyncModelStore<SessionObservable, SessionModelData> {
  sessions: IObservableArray<SessionObservable> = observable.array([]);
  liveQuerySubscription: Maybe<Subscription>;
  private initialLoaded: boolean = false;

  constructor(injectedDeps: AppSubStoreArgs) {
    super({ modelKind: SyncModelKind.Session, ...injectedDeps });
    makeObservable<
      this,
      | "liveQuerySubscription"
      | "subscribeLiveQuery"
      | "unsubscribeLiveQuery"
      | "setIsReady"
      | "initialLoaded"
    >(this, {
      markSessionAsDismissed: true,
      getSessionsExceptByStatus: true,
      getSessionsByStatus: true,
      createSyncModel: false,

      liveQuerySubscription: observable,
      sessions: observable,
      initialLoaded: observable,

      subscribeLiveQuery: action,
      unsubscribeLiveQuery: action,
      setIsReady: action,
      createSession: action,

      isReady: computed,
      allSessions: computed,
    });

    onBecomeObserved(this, "sessions", () => this.subscribeLiveQuery());
    onBecomeUnobserved(this, "sessions", () => this.unsubscribeLiveQuery());
    onBecomeObserved(this, "allSessions", () => this.subscribeLiveQuery());
    onBecomeUnobserved(this, "allSessions", () => this.unsubscribeLiveQuery());
  }

  get isReady() {
    return this.initialLoaded;
  }

  get allSessions(): SessionObservable[] {
    return this.sessions;
  }

  getSessionsByStatus(status: SessionStatus): SessionObservable[] {
    return this.sessions.filter(session => session.status === status);
  }

  getSessionsExceptByStatus(status: SessionStatus): SessionObservable[] {
    return this.sessions.filter(session => session.status !== status);
  }

  createSyncModel(data: SyncUpdateValue<SessionModelData>): SessionObservable {
    return new SessionObservable({
      id: data.model_id,
      data,
      store: this.store,
    });
  }

  /**
   * Creates a new session.
   *
   * @returns The ID of the newly created session
   */
  public async createSession({
    title,
    status,
    kind,
  }: {
    title: string;
    status: SessionStatus;
    kind: SessionKind;
  }): Promise<Uuid> {
    const sessionId = uuidModule.generate();

    await new CreateSessionOperation({
      store: this.store,
      payload: {
        id: sessionId,
        title,
        status,
        kind,
      },
    }).execute();

    return sessionId;
  }

  public async markSessionAsDismissed(sessionId: Uuid) {
    return new UpdateSessionOperation({
      store: this.store,
      payload: { id: sessionId, status: "DISMISSED" },
    }).execute();
  }

  private subscribeLiveQuery() {
    this.setIsReady(false);
    this.liveQuerySubscription?.unsubscribe();
    this.liveQuerySubscription = liveQuery(() => {
      // Get all session IDs from the local Dexie table
      return this.localTable.toCollection().primaryKeys();
    }).subscribe({
      next: async ids => {
        const sessions = await this.getManyAsync(ids as string[]);
        runInAction(() => {
          this.sessions.replace(sessions);
          this.setIsReady(true);
        });
      },
    });
  }

  private unsubscribeLiveQuery() {
    this.setIsReady(false);
    this.liveQuerySubscription?.unsubscribe();
  }

  private setIsReady(value: boolean) {
    this.initialLoaded = value;
  }
}
